import React, { useState, useEffect } from 'react';
import { useWallet } from '../contexts/WalletContext';
import './WalletSelector.css';

interface WalletSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectWallet: (provider: any, providerName: string) => void;
}

const WalletSelector: React.FC<WalletSelectorProps> = ({ isOpen, onClose, onSelectWallet }) => {
  const { getAvailableProviders } = useWallet();
  const [availableProviders, setAvailableProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      const providers = getAvailableProviders();
      setAvailableProviders(providers);
      setLoading(false);
    }
  }, [isOpen, getAvailableProviders]);

  const handleWalletSelect = (provider: any, providerName: string) => {
    onSelectWallet(provider, providerName);
    onClose();
  };

  const getWalletIcon = (providerName: string) => {
    // Map provider names to their corresponding icon files
    const iconMap: { [key: string]: string } = {
      'MetaMask': '/wallet-icons/metamask.svg',
      'Phantom': '/wallet-icons/phantom.svg',
      'Coinbase Wallet': '/wallet-icons/coinbase.svg',
      'Brave Wallet': '/wallet-icons/brave.svg',
      'Trust Wallet': '/wallet-icons/trust.svg',
      'Rabby': '/wallet-icons/rabby.svg',
      'Frame': '/wallet-icons/frame.svg',
      'Tally': '/wallet-icons/tally.svg',
      'Rainbow': '/wallet-icons/rainbow.svg',
      'WalletConnect': '/wallet-icons/walletconnect.svg',
    };

    const iconPath = iconMap[providerName] || '/wallet-icons/default.svg';
    
    return (
      <img 
        src={iconPath} 
        alt={`${providerName} wallet icon`}
        width="24" 
        height="24"
        style={{ borderRadius: '4px' }}
      />
    );
  };

  const getWalletDescription = (providerName: string) => {
    const descriptions: { [key: string]: string } = {
      'MetaMask': 'Connect using MetaMask browser extension',
      'Freighter': 'Connect using Freighter (Stellar / Soroban)',
      'Phantom': 'Connect using Phantom wallet',
      'Coinbase Wallet': 'Connect using Coinbase Wallet',
      'Brave Wallet': 'Connect using Brave browser wallet',
      'Trust Wallet': 'Connect using Trust Wallet',
      'Rabby': 'Connect using Rabby wallet',
      'Frame': 'Connect using Frame wallet',
      'Tally': 'Connect using Tally wallet',
      'Rainbow': 'Connect using Rainbow wallet',
      'WalletConnect': 'Connect using WalletConnect (coming soon)',
      'imToken': 'Connect using imToken wallet',
      'Tokenary': 'Connect using Tokenary wallet',
      'Alpha Wallet': 'Connect using Alpha Wallet',
      'AToken': 'Connect using AToken wallet',
      'Bitpie': 'Connect using Bitpie wallet',
      'BlockWallet': 'Connect using BlockWallet',
      'Dapper': 'Connect using Dapper wallet',
      'Defiant': 'Connect using Defiant wallet',
      'Huobi Wallet': 'Connect using Huobi Wallet',
      'HyperPay': 'Connect using HyperPay wallet',
      'KuCoin Wallet': 'Connect using KuCoin Wallet',
      'Math Wallet': 'Connect using Math Wallet',
      'MyEtherWallet': 'Connect using MyEtherWallet',
      'Opera': 'Connect using Opera wallet',
      'Ownbit': 'Connect using Ownbit wallet',
      'Status': 'Connect using Status wallet',
      'Torus': 'Connect using Torus wallet',
      'Trezor': 'Connect using Trezor hardware wallet',
      'Wallet.io': 'Connect using Wallet.io',
      'XDEFI': 'Connect using XDEFI wallet',
      'Zerion': 'Connect using Zerion wallet',
    };

    return descriptions[providerName] || 'Connect using this wallet';
  };

  const isSupportedWallet = (name: string) => {
    return name === 'MetaMask' || name === 'Freighter';
  };

  const isWalletConnect = (providerName: string) => {
    return providerName === 'WalletConnect';
  };

  if (!isOpen) return null;

  return (
    <div className="wallet-selector-overlay" onClick={onClose} data-testid="wallet-selector-overlay">
      <div className="wallet-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wallet-selector-header">
          <h3>Select Wallet</h3>
          <button className="wallet-selector-close" onClick={onClose}>
            ✕
          </button>
        </div>
        
        <div className="wallet-selector-content">
          {loading ? (
            <div className="wallet-selector-loading">
              <div className="loading-spinner"></div>
              <p>Detecting available wallets...</p>
            </div>
          ) : availableProviders.length === 0 ? (
            <div className="wallet-selector-empty">
              <div className="empty-icon">🔍</div>
              <h4>No Wallets Detected</h4>
              <p>Please install MetaMask to continue. Other wallets will be supported soon.</p>
              <div className="wallet-install-links">
                <a 
                  href="https://metamask.io/download/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="wallet-install-link"
                >
                  Install MetaMask
                </a>
              </div>
            </div>
          ) : (
            <div className="wallet-list">
              {(() => {
                // Create a combined list with detected providers + always-visible WalletConnect
                const hasWalletConnect = availableProviders.some(p => isWalletConnect(p.name));
                const allProviders = [...availableProviders];
                
                // Add WalletConnect if not already detected
                if (!hasWalletConnect) {
                  allProviders.push({
                    provider: null, // No actual provider for WalletConnect
                    name: 'WalletConnect',
                    isAvailable: false
                  });
                }
                
                return allProviders.map((providerInfo, index) => {
                  const isComingSoon = !isSupportedWallet(providerInfo.name);
                  return (
                    <button
                      key={index}
                      className={`wallet-option ${isComingSoon ? 'coming-soon' : ''}`}
                      onClick={() => !isComingSoon && handleWalletSelect(providerInfo.provider, providerInfo.name)}
                      disabled={isComingSoon}
                    >
                      <div className="wallet-option-icon">
                        {getWalletIcon(providerInfo.name)}
                      </div>
                      <div className="wallet-option-info">
                        <div className="wallet-option-name">
                          {providerInfo.name}
                          {isComingSoon && (
                            <span className="coming-soon-badge">Coming Soon</span>
                          )}
                        </div>
                        <div className="wallet-option-description">
                          {isComingSoon 
                            ? 'This wallet will be supported soon' 
                            : getWalletDescription(providerInfo.name)
                          }
                        </div>
                      </div>
                      <div className="wallet-option-arrow">
                        {isComingSoon ? '⏳' : '→'}
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          )}
        </div>
        
        <div className="wallet-selector-footer">
          <p className="wallet-selector-help">
            Supporting MetaMask (EVM) and Freighter (Stellar).{' '}
            <a href="https://metamask.io/download/" target="_blank" rel="noopener noreferrer">Install MetaMask</a>
            {' · '}
            <a href="https://www.freighter.app/" target="_blank" rel="noopener noreferrer">Install Freighter</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default WalletSelector;